/**
 * Recycle Bin page — lists soft-deleted items with restore/delete actions.
 * Supports bulk operations and double-confirmation for destructive actions.
 */
import { useState } from 'react';
import { Trash2, RotateCcw, AlertTriangle, Eye } from 'lucide-react';
import { useI18n } from '@/i18n';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Button,
  Input,
  Spinner,
} from '@/components/ui';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useRecycleBin,
  useRestoreItem,
  useHardDeleteItem,
  useBulkAction,
  useEmptyBin,
  useGlobalConversationBin,
} from '@/features/recycle-bin';
import type { RecycleBinItem, RecycleBinItemType } from '@/features/recycle-bin';
import { toast } from 'sonner';

// Label map for item types
const TYPE_LABELS: Record<RecycleBinItemType, string> = {
  collection: 'Collection',
  agent: 'Agent',
  hive: 'Hive',
  mcp_server: 'Integration',
  secret: 'Secret',
  conversation: 'Conversation',
};

/** Row shape for the unified list (main bin items + tenant-wide conversations). */
type BinRow = RecycleBinItem & { ownerEmail?: string };

type ActionTarget = { item: RecycleBinItem; action: 'restore' | 'delete' };
type BulkConfirm = { action: 'restore' | 'delete' };

const EMPTY_BIN_CONFIRMATION = 'EMPTY';

export function RecycleBinPage() {
  const { t } = useI18n();
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmTarget, setConfirmTarget] = useState<ActionTarget | null>(null);
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState<BulkConfirm | null>(null);

  // Double-confirmation text inputs
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [emptyConfirmText, setEmptyConfirmText] = useState('');
  const [bulkDeleteConfirmText, setBulkDeleteConfirmText] = useState('');

  const { data, isLoading, error } = useRecycleBin({ page, page_size: 20 });
  const restoreItem = useRestoreItem();
  const deleteItem = useHardDeleteItem();
  const bulkAction = useBulkAction();
  const emptyBin = useEmptyBin();

  // Tenant-wide conversations (REQ-CHAT-020) — merged into the SAME list.
  // Read-only (bin_viewers): no restore/delete, no selection. Hidden on 403.
  const globalBin = useGlobalConversationBin(1, 100);
  const globalDenied =
    (globalBin.error as { response?: { status?: number } } | null)?.response?.status === 403;
  const mainItems: RecycleBinItem[] = data?.data ?? [];
  const conversationItems: RecycleBinItem[] =
    globalDenied || !globalBin.data
      ? []
      : globalBin.data.data.map((c) => ({
          id: c.id,
          type: 'conversation',
          name: c.title || t('admin.recycleBin.untitledConversation'),
          deleted_at: c.archived_at ?? '',
          days_until_purge: c.purge_at
            ? Math.max(
                0,
                Math.ceil(
                  (new Date(c.purge_at).getTime() - Date.now()) / (24 * 60 * 60 * 1000)
                )
              )
            : 0,
          tenant_id: '',
          ownerEmail: c.user_email ?? undefined,
        }));

  const items: BinRow[] = [...mainItems, ...conversationItems];
  const meta = data?.meta;

  const selectedItems = items.filter(i => selectedIds.has(i.id));

  // ---------- selection ----------
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () =>
    setSelectedIds(new Set(mainItems.map(i => i.id)));
  const clearSelection = () => setSelectedIds(new Set());

  // ---------- single actions ----------
  const handleConfirm = async () => {
    if (!confirmTarget) return;
    const { item, action } = confirmTarget;
    try {
      if (action === 'restore') {
        await restoreItem.mutateAsync({ itemType: item.type, itemId: item.id });
        toast.success(t('admin.recycleBin.restoreSuccess', { name: item.name }));
      } else {
        await deleteItem.mutateAsync({ itemType: item.type, itemId: item.id });
        toast.success(t('admin.recycleBin.deleteSuccess', { name: item.name }));
      }
    } catch {
      toast.error(
        action === 'restore'
          ? t('admin.recycleBin.restoreFailed')
          : t('admin.recycleBin.deleteFailed'),
      );
    } finally {
      setConfirmTarget(null);
      setDeleteConfirmText('');
    }
  };

  // ---------- bulk actions ----------
  const handleBulkConfirm = async () => {
    if (!bulkConfirm || selectedItems.length === 0) return;
    try {
      const result = await bulkAction.mutateAsync({
        action: bulkConfirm.action,
        items: selectedItems.map(i => ({ id: i.id, type: i.type })),
      });
      const { succeeded, failed } = result.summary;
      if (failed === 0) {
        toast.success(t('admin.recycleBin.bulkSuccess', { count: succeeded }));
      } else {
        toast.warning(t('admin.recycleBin.bulkPartial', { succeeded, failed }));
      }
      clearSelection();
    } catch {
      toast.error(t('admin.recycleBin.bulkFailed'));
    } finally {
      setBulkConfirm(null);
      setBulkDeleteConfirmText('');
    }
  };

  const handleEmptyBin = async () => {
    try {
      const result = await emptyBin.mutateAsync();
      toast.success(t('admin.recycleBin.emptySuccess', { count: result.deleted_count }));
    } catch {
      toast.error(t('admin.recycleBin.emptyFailed'));
    } finally {
      setConfirmEmpty(false);
      setEmptyConfirmText('');
    }
  };

  // ---------- urgency colour ----------
  const urgencyClass = (days: number) => {
    if (days <= 3) return 'text-red-600 dark:text-red-400 font-semibold';
    if (days <= 7) return 'text-orange-500 dark:text-orange-400';
    return 'text-gray-500 dark:text-gray-400';
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-300">
            {error instanceof Error ? error.message : t('common.genericError')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="recycle-bin-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Trash2 className="h-6 w-6" />
            {t('admin.recycleBin.title')}
          </h1>
          <p className="text-gray-500 mt-1">{t('admin.recycleBin.subtitle')}</p>
        </div>
        {mainItems.length > 0 && (
          <Button
            variant="destructive"
            onClick={() => setConfirmEmpty(true)}
            disabled={emptyBin.isPending}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {t('admin.recycleBin.emptyBin')}
          </Button>
        )}
      </div>

      {/* Retention notice */}
      <div className="mb-4 flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
        {t('admin.recycleBin.retentionNotice')}
      </div>

      {/* Bulk action bar — visible when items are selected */}
      {selectedIds.size > 0 && (
        <div className="mb-4 flex items-center gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
            {t('admin.recycleBin.bulkSelected', { count: selectedIds.size })}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setBulkConfirm({ action: 'restore' })}
            disabled={bulkAction.isPending}
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            {t('admin.recycleBin.bulkRestore')}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setBulkConfirm({ action: 'delete' })}
            disabled={bulkAction.isPending}
          >
            <Trash2 className="h-3 w-3 mr-1" />
            {t('admin.recycleBin.bulkDelete')}
          </Button>
          <Button variant="ghost" size="sm" onClick={clearSelection}>
            {t('common.cancel')}
          </Button>
        </div>
      )}

      {/* Table */}
      <Table data-testid="recycle-bin-table">
        <TableHeader>
          <TableRow>
            <TableHead className="w-8">
              <input
                type="checkbox"
                checked={selectedIds.size === mainItems.length && mainItems.length > 0}
                onChange={() => selectedIds.size === mainItems.length ? clearSelection() : selectAll()}
                className="rounded border-gray-300"
              />
            </TableHead>
            <TableHead>{t('admin.recycleBin.table.name')}</TableHead>
            <TableHead>{t('admin.recycleBin.table.type')}</TableHead>
            <TableHead>{t('admin.recycleBin.table.deletedAt')}</TableHead>
            <TableHead>{t('admin.recycleBin.table.daysLeft')}</TableHead>
            <TableHead className="w-32"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableEmpty
              colSpan={6}
              message={t('admin.recycleBin.empty')}
            />
          ) : (
            items.map((item) => (
              <TableRow key={item.id} className={selectedIds.has(item.id) ? 'bg-blue-50 dark:bg-blue-900/10' : ''}>
                <TableCell>
                  {item.type === 'conversation' ? (
                    <span className="inline-block w-4" aria-hidden="true" />
                  ) : (
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item.id)}
                      onChange={() => toggleSelect(item.id)}
                      className="rounded border-gray-300"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{item.name}</span>
                  <span className="ml-2 text-xs text-gray-400">
                    {item.type === 'conversation' ? (item.ownerEmail ?? '—') : item.id}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                    {TYPE_LABELS[item.type]}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-gray-500 dark:text-gray-400">
                  {item.deleted_at
                    ? new Date(item.deleted_at).toLocaleDateString()
                    : '—'}
                </TableCell>
                <TableCell>
                  <span className={`text-sm ${urgencyClass(item.days_until_purge)}`}>
                    {item.days_until_purge}d
                  </span>
                </TableCell>
                <TableCell>
                  {item.type === 'conversation' ? (
                    <div
                      className="flex items-center gap-1 text-gray-400 dark:text-gray-500"
                      title={t('admin.recycleBin.conversationReadOnly')}
                    >
                      <Eye className="h-3.5 w-3.5" />
                      <span className="text-xs">{t('admin.recycleBin.readOnly')}</span>
                    </div>
                  ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirmTarget({ item, action: 'restore' })}
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      {t('admin.recycleBin.restore')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setConfirmTarget({ item, action: 'delete' })}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {meta && meta.total_pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-500">
            {t('common.showing', {
              from: (meta.page - 1) * meta.page_size + 1,
              to: Math.min(meta.page * meta.page_size, meta.total),
              total: meta.total,
            })}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              {t('common.previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= meta.total_pages}
              onClick={() => setPage(p => p + 1)}
            >
              {t('common.next')}
            </Button>
          </div>
        </div>
      )}

      {/* ==================== DIALOGS ==================== */}

      {/* Single item confirm dialog — restore (simple confirm) */}
      <AlertDialog
        open={confirmTarget?.action === 'restore'}
        onOpenChange={(open) => { if (!open) setConfirmTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.recycleBin.confirmRestoreTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.recycleBin.confirmRestoreDesc', { name: confirmTarget?.item.name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={restoreItem.isPending}
            >
              {t('admin.recycleBin.restore')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Single item confirm dialog — delete (type name to confirm) */}
      <AlertDialog
        open={confirmTarget?.action === 'delete'}
        onOpenChange={(open) => { if (!open) { setConfirmTarget(null); setDeleteConfirmText(''); } }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.recycleBin.confirmDeleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.recycleBin.confirmDeleteDesc', { name: confirmTarget?.item.name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">
              {t('admin.recycleBin.confirmDeleteTypeName', { name: confirmTarget?.item.name ?? '' })}
            </label>
            <Input
              className="mt-1"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={confirmTarget?.item.name ?? ''}
              autoComplete="off"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteItem.isPending || deleteConfirmText !== confirmTarget?.item.name}
            >
              {t('admin.recycleBin.permanentlyDelete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk restore confirm (simple confirm) */}
      <AlertDialog
        open={bulkConfirm?.action === 'restore'}
        onOpenChange={(open) => { if (!open) setBulkConfirm(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.recycleBin.confirmBulkRestoreTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.recycleBin.confirmBulkRestoreDesc', { count: selectedIds.size })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkConfirm}
              disabled={bulkAction.isPending}
            >
              {t('admin.recycleBin.bulkRestore')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirm (type count to confirm) */}
      <AlertDialog
        open={bulkConfirm?.action === 'delete'}
        onOpenChange={(open) => { if (!open) { setBulkConfirm(null); setBulkDeleteConfirmText(''); } }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.recycleBin.confirmBulkDeleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.recycleBin.confirmBulkDeleteDesc', { count: selectedIds.size })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">
              {t('admin.recycleBin.confirmBulkDeleteType', { text: `DELETE ${selectedIds.size}` })}
            </label>
            <Input
              className="mt-1"
              value={bulkDeleteConfirmText}
              onChange={(e) => setBulkDeleteConfirmText(e.target.value)}
              placeholder={`DELETE ${selectedIds.size}`}
              autoComplete="off"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkConfirm}
              className="bg-red-600 hover:bg-red-700"
              disabled={bulkAction.isPending || bulkDeleteConfirmText !== `DELETE ${selectedIds.size}`}
            >
              {t('admin.recycleBin.bulkDelete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Empty bin confirm dialog (type EMPTY to confirm) */}
      <AlertDialog open={confirmEmpty} onOpenChange={(open) => { if (!open) { setConfirmEmpty(false); setEmptyConfirmText(''); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.recycleBin.confirmEmptyTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.recycleBin.confirmEmptyDesc', { count: meta?.total ?? 0 })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">
              {t('admin.recycleBin.confirmEmptyType', { text: EMPTY_BIN_CONFIRMATION })}
            </label>
            <Input
              className="mt-1"
              value={emptyConfirmText}
              onChange={(e) => setEmptyConfirmText(e.target.value)}
              placeholder={EMPTY_BIN_CONFIRMATION}
              autoComplete="off"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleEmptyBin}
              className="bg-red-600 hover:bg-red-700"
              disabled={emptyBin.isPending || emptyConfirmText !== EMPTY_BIN_CONFIRMATION}
            >
              {emptyBin.isPending ? t('common.deleting') : t('admin.recycleBin.emptyBin')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
